import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';

import Progress from 'components/common/Progress';
import Button from 'components/common/Button';

import formatPrice from 'utils/formatPrice';
import formatSinglePlural from 'utils/formatSinglePlural';
import hexToRgb from 'styles/hexToRgb';
import pxToRem from 'styles/pxToRem';

import dateFormat from 'config/dateFormat';

const moment = extendMoment(Moment);

const styles = theme => ({
  root: {
    padding: '0 16px',
    height: 298,
    minHeight: 298,
    position: 'relative',
    overflow: 'visible',
  },
  tooltip: {
    position: 'absolute',
    zIndex: 3,
    boxSizing: 'border-box',
    padding: '8px 16px',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.primary.A100}`,
    borderRadius: 4,
    pointerEvents: 'auto',
    whiteSpace: 'nowrap',
    fontFamily: theme.typography.fontFamily,
  },
  tooltipAbove: {
    transform: 'translate(-50%, calc(-100% - 16px))',
  },
  tooltipBelow: {
    transform: 'translate(-50%, 16px)',
  },
  tooltipTitle: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingBottom: 8,
    marginBottom: 8,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  tooltipCharge: {
    fontSize: pxToRem(12),
    fontWeight: 700,
    lineHeight: '12px',
    color: theme.palette.secondary[650],
  },
  tooltipDate: {
    marginTop: 2,
    marginLeft: 6,
    fontSize: pxToRem(10),
    fontWeight: 400,
    lineHeight: '10px',
    color: theme.palette.grey[700],
  },
  tooltipBody: {
    fontSize: pxToRem(12),
    lineHeight: '16px',
    color: theme.palette.grey[700],
  },
  tooltipButton: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: 8,
  },
});

function getWorkflowHref({ dateFrom, dateTo, stateId, statusId, stateName, statusName }) {
  const params = [
    ...dateFrom ? [`date-of-creation-from=${dateFrom}`] : [],
    ...dateTo ? [`date-of-creation-to=${dateTo}`] : [],
    ...stateId ? [`state=${stateId}`] : [],
    ...statusId ? [`status=${statusId}`] : [],
    ...stateName ? [`state-name=${stateName}`] : [],
    ...statusName ? [`status-name=${statusName}`] : [],
  ];
  return params.length > 0 ? `/workflow?${params.join('&')}` : '/workflow';
}

class StatsLineChartCanvas extends Component {
  shouldComponentUpdate(nextProps) {
    return nextProps.Line !== this.props.Line
      || nextProps.statistics !== this.props.statistics
      || nextProps.tickStep !== this.props.tickStep
      || nextProps.dateStep !== this.props.dateStep
      || nextProps.width !== this.props.width
      || nextProps.theme !== this.props.theme;
  }

  render() {
    const {
      Line,
      statistics,
      theme,
      tickStep,
      dateStep,
      width,
      onTooltipCustom,
      chartRef,
    } = this.props;
    const data = (canvas) => {
      const ctx = canvas.getContext('2d');
      const grd = ctx.createLinearGradient(150.000, 0.000, 150.000, 300.000);
      grd.addColorStop(0.000, `rgba(${hexToRgb(theme.palette.primary[150])}, 0.4)`);
      grd.addColorStop(1.000, `rgba(${hexToRgb(theme.palette.primary[150])}, 0)`);
      return {
        labels: statistics.map(item => item.date),
        bezierCurve: false,
        datasets: [
          {
            data: statistics.map(item => ({
              ...item,
              y: Number(item.total_charges),
            })),
            lineTension: 0.1,
            backgroundColor: grd,
            borderColor: theme.palette.primary[150],
            borderCapStyle: 'butt',
            borderDash: [],
            borderWidth: 2,
            borderDashOffset: 0,
            borderJoinStyle: 'miter',
            pointBackgroundColor: 'transparent',
            pointHoverRadius: 8,
            pointHoverBackgroundColor: 'transparent',
            pointHoverBorderColor: 'transparent',
            pointHoverBorderWidth: 1,
            pointRadius: 8,
            pointHitRadius: 8,
            ...statistics.length > 1 ? {
              pointRadius: 0,
              pointBorderWidth: 0,
            } : {
              pointRadius: 8,
              pointBorderWidth: 1,
            },
          },
        ],
      };
    };
    const options = {
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
      },
      legend: {
        display: false,
      },
      scales: {
        xAxes: [{
          offset: true,
          gridLines: {
            drawOnChartArea: false,
            tickMarkLength: 8,
          },
          ticks: {
            fontColor: theme.palette.grey[700],
            fontSize: 12,
            maxRotation: 0,
            autoSkip: false,
            padding: 8,
            maxRotation: ['xs', 'sm', 'md', 'lg'].indexOf(width) !== -1 ? 90 : 0,
            minRotation: ['xs', 'sm', 'md', 'lg'].indexOf(width) !== -1 ? 90 : 0,
            fontFamily: theme.typography.fontFamily,
            callback: (value, index) => {
              const momentDate = moment(value, 'MM/DD/YYYY');
              if (tickStep === 'month') {
                if (dateStep === 'week') {
                  return index % 4 === 0
                    ? momentDate.format('MMM D') : null;
                }
                return [
                  ' ',
                  momentDate.format('MMM'),
                ];
              }
              if (tickStep === '2 months') {
                return index % 2 === 0
                  ? momentDate.format('MMM') : null;
              }
              if (tickStep === 'week') {
                if (dateStep === 'day') {
                  return index % 7 === 0
                    ? momentDate.format('MMM D') : null;
                }
                return momentDate.format('MMM D');
              }
              if (tickStep === '2 weeks') {
                return index % 2 === 0
                  ? momentDate.format('MMM D') : null;
              }
              return [
                momentDate.format('ddd').toUpperCase(),
                momentDate.format(dateFormat),
              ];
            },
          },
        }],
        yAxes: [{
          gridLines: {
            borderDash: [8, 5],
            drawTicks: false,
            color: theme.palette.primary.A100,
            zeroLineColor: theme.palette.primary.A100,
            drawBorder: false,
          },
          ticks: {
            fontColor: theme.palette.grey[700],
            fontSize: 12,
            fontFamily: theme.typography.fontFamily,
            padding: 16,
            beginAtZero: true,
            callback: value => (value === 0 ? '' : formatPrice(value)),
            minor: {
              color: 'red',
            },
          },
        }],
      },
      tooltips: {
        enabled: false,
        mode: 'label',
        axis: 'x',
        intersect: false,
        custom: onTooltipCustom,
      },
    };
    return (
      <Line
        id="lineChart"
        ref={chartRef}
        data={data}
        options={options}
      />
    );
  }
}

StatsLineChartCanvas.propTypes = {
  Line: PropTypes.func.isRequired,
  statistics: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    patients: PropTypes.number,
    total_charges: PropTypes.string,
    order_line_counts: PropTypes.number,
  })).isRequired,
  tickStep: PropTypes.string.isRequired,
  dateStep: PropTypes.string.isRequired,
  width: PropTypes.string.isRequired,
  onTooltipCustom: PropTypes.func.isRequired,
  chartRef: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({
        150: PropTypes.string.isRequired,
        A100: PropTypes.string.isRequired,
      }).isRequired,
      grey: PropTypes.shape({
        700: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

class StatsLineChartChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      Line: null,
      tooltip: null,
    };

    this.tooltipHovered = false;
    this.hideTooltipTimeout = null;

    this.importDependencies = this.importDependencies.bind(this);
    this.onTooltipCustom = this.onTooltipCustom.bind(this);
    this.onTooltipMouseEnter = this.onTooltipMouseEnter.bind(this);
    this.onTooltipMouseLeave = this.onTooltipMouseLeave.bind(this);
  }

  componentDidMount() {
    this.importDependencies();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.loading > 0 && this.state.tooltip) {
      this.setState({ tooltip: null });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.hideTooltipTimeout);
  }

  onTooltipCustom(tooltipModel) {
    if (!tooltipModel || tooltipModel.opacity === 0) {
      clearTimeout(this.hideTooltipTimeout);
      this.hideTooltipTimeout = setTimeout(() => {
        if (!this.tooltipHovered) {
          this.setState({ tooltip: null });
        }
      }, 150);
      return;
    }
    if (this.tooltipHovered) {
      return;
    }
    clearTimeout(this.hideTooltipTimeout);
    if (!tooltipModel.dataPoints || !tooltipModel.dataPoints.length) {
      return;
    }
    const { statistics } = this.props;
    const index = tooltipModel.dataPoints[0].index;
    const point = statistics[index];
    if (!point || !this.chartRef || !this.chartRef.chartInstance || !this.rootRef) {
      return;
    }
    const canvasRect = this.chartRef.chartInstance.canvas.getBoundingClientRect();
    const rootRect = this.rootRef.getBoundingClientRect();
    const x = (canvasRect.left - rootRect.left) + tooltipModel.caretX;
    const y = (canvasRect.top - rootRect.top) + tooltipModel.caretY;
    const { tooltip } = this.state;
    if (tooltip
      && tooltip.index === index
      && tooltip.x === x
      && tooltip.y === y) {
      return;
    }
    this.setState({
      tooltip: {
        index,
        x,
        y,
        placement: y < 120 ? 'below' : 'above',
        date: point.date,
        dateTo: point.dateTo || point.date,
        total_charges: point.total_charges,
        order_line_counts: point.order_line_counts,
        patients: point.patients,
      },
    });
  }

  onTooltipMouseEnter() {
    this.tooltipHovered = true;
    clearTimeout(this.hideTooltipTimeout);
  }

  onTooltipMouseLeave() {
    this.tooltipHovered = false;
    this.setState({ tooltip: null });
  }

  async importDependencies() {
    const { theme } = this.props;
    const { Chart, Line } = await import('react-chartjs-2');
    const originalLineDraw = Chart.controllers.line.prototype.draw;
    Chart.helpers.extend(Chart.controllers.line.prototype, {
      draw: function draw(ease) {
        originalLineDraw.call(this, ease);
        let activePoint;
        if (this.chart.getDatasetMeta(0).data.length === 1) {
          activePoint = this.chart.getDatasetMeta(0).data[0];
        }
        // eslint-disable-next-line no-underscore-dangle
        if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
          // eslint-disable-next-line no-underscore-dangle
          activePoint = this.chart.tooltip._active[0];
        }
        if (activePoint) {
          const ctx = this.chart.ctx;
          const x = activePoint.tooltipPosition().x;
          const y = activePoint.tooltipPosition().y;
          const topY = this.chart.scales['y-axis-0'].top;
          const bottomY = this.chart.scales['y-axis-0'].bottom;
          ctx.save();
          ctx.setLineDash([8, 5]);
          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.lineWidth = 1;
          ctx.strokeStyle = theme.palette.primary[150];
          ctx.stroke();
          ctx.restore();
          ctx.save();
          ctx.beginPath();
          ctx.shadowBlur = 4;
          ctx.shadowColor = theme.palette.primary.A100;
          ctx.arc(x, y, 8, 0, 2 * Math.PI, false);
          ctx.fillStyle = theme.palette.common.white;
          ctx.fill();
          ctx.restore();
          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, 2 * Math.PI, false);
          ctx.fillStyle = theme.palette.primary[150];
          ctx.fill();
          ctx.restore();
        }
      },
    });
    this.setState({
      Line,
    });
  }

  render() {
    const {
      statistics,
      loading,
      theme,
      tickStep,
      dateStep,
      classes,
      width,
      workflowStateId,
      workflowStatusId,
      workflowStateName,
      workflowStatusName,
    } = this.props;
    const { Line, tooltip } = this.state;
    return (
      <div
        className={classes.root}
        ref={(rootRef) => {
          this.rootRef = rootRef;
        }}
      >
        {(!Line || loading > 0) ? (
          <Progress />
        ) : (
          <StatsLineChartCanvas
            Line={Line}
            statistics={statistics}
            theme={theme}
            tickStep={tickStep}
            dateStep={dateStep}
            width={width}
            onTooltipCustom={this.onTooltipCustom}
            chartRef={(chartRef) => {
              this.chartRef = chartRef;
            }}
          />
        )}
        {tooltip &&
          <div
            className={classNames(classes.tooltip, {
              [classes.tooltipAbove]: tooltip.placement === 'above',
              [classes.tooltipBelow]: tooltip.placement === 'below',
            })}
            style={{
              left: tooltip.x,
              top: tooltip.y,
            }}
            onMouseEnter={this.onTooltipMouseEnter}
            onMouseLeave={this.onTooltipMouseLeave}
          >
            <div className={classes.tooltipTitle}>
              <span className={classes.tooltipCharge}>
                {formatPrice(Number(tooltip.total_charges))}
              </span>
              <span className={classes.tooltipDate}>
                {tooltip.date}
                {(tooltip.dateTo && tooltip.dateTo !== tooltip.date) ? `-${tooltip.dateTo}` : ''}
              </span>
            </div>
            <div className={classes.tooltipBody}>
              {`${tooltip.order_line_counts} ${formatSinglePlural({
                quantity: tooltip.order_line_counts,
                single: 'product',
                plural: 'products',
              })} | ${tooltip.patients} ${formatSinglePlural({
                quantity: tooltip.patients,
                single: 'patient',
                plural: 'patients',
              })}`}
            </div>
            <Button
              className={classes.tooltipButton}
              href={getWorkflowHref({
                dateFrom: tooltip.date,
                dateTo: tooltip.dateTo || tooltip.date,
                stateId: workflowStateId,
                statusId: workflowStatusId,
                stateName: workflowStateName,
                statusName: workflowStatusName,
              })}
              target="_blank"
              variant="raised"
              color="primary"
              size="sm"
            >
              View orders
            </Button>
          </div>
        }
      </div>
    );
  }
}

StatsLineChartChart.propTypes = {
  statistics: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    dateTo: PropTypes.string,
    patients: PropTypes.number,
    total_charges: PropTypes.string,
    order_line_counts: PropTypes.number,
  })).isRequired,
  tickStep: PropTypes.string.isRequired,
  dateStep: PropTypes.string.isRequired,
  loading: PropTypes.number.isRequired,
  workflowStateId: PropTypes.number,
  workflowStatusId: PropTypes.number,
  workflowStateName: PropTypes.string,
  workflowStatusName: PropTypes.string,
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({
        150: PropTypes.string.isRequired,
        500: PropTypes.string.isRequired,
        A100: PropTypes.string.isRequired,
        A400: PropTypes.string.isRequired,
      }).isRequired,
      secondary: PropTypes.shape({
        650: PropTypes.string.isRequired,
      }).isRequired,
      grey: PropTypes.shape({
        700: PropTypes.string.isRequired,
      }).isRequired,
      common: PropTypes.shape({
        white: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    tooltipAbove: PropTypes.string.isRequired,
    tooltipBelow: PropTypes.string.isRequired,
    tooltipTitle: PropTypes.string.isRequired,
    tooltipCharge: PropTypes.string.isRequired,
    tooltipDate: PropTypes.string.isRequired,
    tooltipBody: PropTypes.string.isRequired,
    tooltipButton: PropTypes.string.isRequired,
  }).isRequired,
};

StatsLineChartChart.defaultProps = {
  workflowStateId: null,
  workflowStatusId: null,
  workflowStateName: null,
  workflowStatusName: null,
};

export default withWidth()(withStyles(styles, { withTheme: true })(StatsLineChartChart));



// WEBPACK FOOTER //
// ./src/components/home/StatsLineChart/StatsLineChartChart.js